import { useCheckVanityUrlAvailability } from 'api/common/vanity-url/hooks';
import { useFormikContext } from 'formik';
import i18n from 'i18n';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import Spinner from '../Spinner/spinner';
import Form from './form';

const CHECK_DEBOUNCE_MS = 500;
const FALLBACK_UNAVAILABLE_MESSAGE = 'Vanity URL is not available';
const CHECKING_MESSAGE = 'Checking vanity URL availability';
const SLUG_REGEX = /^[a-zA-Z0-9-_]+$/;

const VanityUrlAvailabilityInput = ({
  name,
  append,
  prepend,
  unavailableMessage = FALLBACK_UNAVAILABLE_MESSAGE,
  checkingMessage = CHECKING_MESSAGE,
  invalidMessage = i18n.t('validation:lettersNumbersHyphensAndUnderscoresOnly'),
  onAvailabilityChange,
  required = false,
  validate,
  ...props
}) => {
  const { values, initialValues, errors, validateField } = useFormikContext();
  const [valueToCheck, setValueToCheck] = useState(null);

  const currentValue = values?.[name] || '';
  const initialValue = initialValues?.[name] || '';
  const trimmedValue = currentValue.trim();
  const hasValue = trimmedValue.length > 0;
  const hasChanged = currentValue !== initialValue;
  const isEmptyRequiredValue = required && !hasValue;
  const hasFormatError = hasValue && !SLUG_REGEX.test(currentValue);
  const shouldCheck = hasChanged && hasValue && !hasFormatError;

  const debouncedSetValueToCheck = useMemo(
    () => debounce((value) => setValueToCheck(value), CHECK_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    if (shouldCheck) {
      debouncedSetValueToCheck(trimmedValue);
    } else {
      debouncedSetValueToCheck.cancel();
      setValueToCheck(null);
    }

    return () => {
      debouncedSetValueToCheck.cancel();
    };
  }, [debouncedSetValueToCheck, shouldCheck, trimmedValue]);

  const { data, isError, isFetching } = useCheckVanityUrlAvailability(valueToCheck, {
    enabled: !!valueToCheck && valueToCheck.length > 0,
  });

  const hasCheckedCurrentValue = shouldCheck && valueToCheck === trimmedValue;
  const isCheckingCurrentValue = shouldCheck && !hasCheckedCurrentValue;
  const isLoading = isFetching || isCheckingCurrentValue;
  const availabilityMessage = data?.message || unavailableMessage;
  const hasAvailabilityError = hasCheckedCurrentValue && data?.available === false;
  const hasCheckError = hasCheckedCurrentValue && isError;
  const isCurrentValueAvailable = hasCheckedCurrentValue && data?.available === true;
  const shouldBlockSubmit =
    isEmptyRequiredValue ||
    hasFormatError ||
    hasCheckError ||
    (shouldCheck && (isLoading || hasAvailabilityError));

  useEffect(() => {
    validateField(name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    hasAvailabilityError,
    hasCheckError,
    availabilityMessage,
    hasFormatError,
    isEmptyRequiredValue,
    validateField,
    name,
  ]);

  useEffect(() => {
    onAvailabilityChange?.({
      hasChanged,
      isAvailable: !hasAvailabilityError && !hasCheckError,
      isLoading,
      message: hasAvailabilityError || hasCheckError ? availabilityMessage : null,
      shouldBlockSubmit,
    });
  }, [
    availabilityMessage,
    hasFormatError,
    hasAvailabilityError,
    hasCheckError,
    hasChanged,
    isEmptyRequiredValue,
    isLoading,
    onAvailabilityChange,
    shouldBlockSubmit,
  ]);

  const indicator = isLoading ? (
    <Spinner style={{ width: '17px', height: '17px' }} />
  ) : isCurrentValueAvailable && !errors?.[name] ? (
    <i className="fe fe-check-circle text-success" />
  ) : isEmptyRequiredValue || hasFormatError || hasAvailabilityError || hasCheckError ? (
    <i className="fe fe-x-circle text-danger" />
  ) : null;

  const inputPrepend = prepend || !indicator ? prepend : indicator;
  const inputAppend =
    prepend && indicator ? (
      <>
        {append}
        {indicator}
      </>
    ) : (
      append
    );

  return (
    <Form.Input
      {...props}
      name={name}
      prepend={inputPrepend}
      append={inputAppend}
      required={required}
      validate={(value) => {
        const customError = validate?.(value);
        if (customError) return customError;
        if (value && !SLUG_REGEX.test(value)) return invalidMessage;
        if (!shouldCheck) return undefined;
        if (isLoading) return checkingMessage;
        if (hasCheckError) return availabilityMessage;
        return hasAvailabilityError ? availabilityMessage : undefined;
      }}
    />
  );
};

VanityUrlAvailabilityInput.propTypes = {
  name: PropTypes.string.isRequired,
  append: PropTypes.node,
  prepend: PropTypes.node,
  unavailableMessage: PropTypes.string,
  checkingMessage: PropTypes.string,
  invalidMessage: PropTypes.string,
  onAvailabilityChange: PropTypes.func,
  required: PropTypes.bool,
  validate: PropTypes.func,
};

export default VanityUrlAvailabilityInput;
